import React, { useEffect, useState } from 'react';
import { CollapsablePanel } from '../../collapsable-panel';
import { BaseButton, Flex, Stack, Text } from '../../../vanilla';
import { ChevronUpIcon } from '../../icons';
import { useGlobalStore, useProductStore } from '../../../store/hooks/useStore';
import { ProductModel } from '../../../store/ProductStore';
import { ProductImage } from '../product-image';
import { observer } from 'mobx-react';
import { capitalize, formatProductName } from '../../../utils/utils';
import { Link } from '../../link';
import { formatAllergens, allergensProductMayContain, highlightIngredientsWithAllergens } from '../../../utils/allergen-ingredients-utils';

type BundledProduct = {
  id: string;
  quantity: number;
}

interface BundleItemAccordionProps {
  bundledItems: BundledProduct[] | undefined;
}

export const BundleItemAccordion = observer(({ bundledItems }: BundleItemAccordionProps) => {
  const productIDs = bundledItems && Array.isArray(bundledItems) ? bundledItems.map(bundledItem => bundledItem.id) : []
  const productStore = useProductStore()
  const { getCustomSitePreferenceById, customSitePreferences, isCustomSitePreferenceSet } = useGlobalStore()

  const [globalAllergenWords, setGlobalAllergenWords] = useState<string[]>([]);

  useEffect(() => {
    (async function () {
      if (!isCustomSitePreferenceSet('globalAllergenWords')) {
        await getCustomSitePreferenceById('globalAllergenWords')
      }
      if (Array.isArray(customSitePreferences['globalAllergenWords'])) {
        setGlobalAllergenWords(customSitePreferences['globalAllergenWords'] as Array<string>)
      }
    })()
  }, [])

  useEffect(() => {
    if (productIDs.length) productStore.fetchProducts(productIDs, false)
  }, [productIDs])

  const products = productIDs
    .map(id => productStore.productsById[id])
    .filter((product): product is ProductModel => !!product)

  const getProductDetail = (productId: string): ProductModel | undefined => {
    return products.find(product => product.id === productId)
  }

  const allergens = (id: string): string[] => { return getProductDetail(id)?.allergenContains ?? []; }

  const BundleItemContent = ({ id }: { id: string }) => {
    const bundleItem = getProductDetail(id)
    const productLink = bundleItem?.name ? `/p/${formatProductName(bundleItem?.name)}/${id}.html` : '/'
    const mayContain = allergensProductMayContain(getProductDetail(id)?.allergenMayContain)
    const ingredients = getProductDetail(id)?.ingredients ?? []

    return (
      <Stack gap="16px" paddingX="12px">
        <Text variant="text6" color="gray1000">Ingredients</Text>
        {ingredients?.map((ingredientsText: string, index: number) => (
          <>
            <Text
              key={index}
              variant="text3"
              color="gray1000"
              lineHeight="short"
              dangerouslySetInnerHTML={{
                __html: highlightIngredientsWithAllergens(
                  ingredientsText,
                  [...allergens(id), ...globalAllergenWords],
                ),
              }}
            />
            {allergens(id)?.length > 0 && (
              <Stack>
                <Text variant="text6" fontWeight="normal" fontSize="md" marginBottom={"8px"}>
                  Allergy Information
                </Text>
                <Text variant="text3" color="gray1000" lineHeight="none">
                  Contains: <span style={{ fontWeight: 'bold' }}>
                    {ingredients
                      ? formatAllergens(ingredients.join(', '), allergens(id), globalAllergenWords)
                      : allergens(id).map((allergen: string) => capitalize(allergen)).join(", ")
                    }
                  </span>
                </Text>
                {mayContain && (
                  <Text variant="text3" color="gray1000" lineHeight="none">
                    May contain: <span style={{ fontWeight: 'bold' }}>{mayContain}</span>
                  </Text>
                )}
              </Stack>
            )}
            <BaseButton
              variant='secondary'
              as={Link}
              gap={'10px'}
              width={'fit'}
              to={productLink}
            >
              <Text variant="text3">View Full Details</Text>
            </BaseButton>
          </>
        ))}
      </Stack >
    )
  }

  return (
    <>
      {products.length != 0 && (
        products.map(product => {
          const quantity = bundledItems?.find(item => item.id === product.id)?.quantity
          const img = product.imageGroups?.[0]?.images?.[0] || product.image_groups?.[0]?.images?.[0]
          return (
            <CollapsablePanel
              key={product.id}
              defaultOpen={false}
              panelContent={<BundleItemContent id={product.id} />}
              border="1px"
              paddingY="10px"
              paddingX="12px"
              borderColor="gray200"
              spacing={20}
              gap="10px"
            >
              {({ onToggle, isOpen }) => (
                <>
                  <Flex
                    borderRadius="none"
                    height="auto"
                    width="full"
                    alignItems="center"
                    onClick={onToggle}
                    borderBottom={isOpen ? "1px" : "0px"}
                    gap={"10px"}
                    borderColor="gray200"
                    paddingY="16px"
                  >
                    <ProductImage
                      image={img}
                      imageAlt={img?.alt}
                      isThumbnail={true}
                      inDrawer={false}
                      minHeight={['auto', 'full']}
                      style={{ objectFit: 'contain', width: '60px', height: '60px' }}
                      alignSelf={'flex-start'}
                    />
                    <Text variant="text6">{`${quantity}x ${product.name}`}</Text>
                    <ChevronUpIcon
                      boxSize="20px"
                      gap={"10px"}
                      minWidth="20px"
                      style={{
                        transform: `rotate(${isOpen ? 0 : 180}deg)`,
                        transition: 'transform 200ms ease-out',
                        marginLeft: 'auto',
                      }}
                    />
                  </Flex>
                </>
              )}
            </CollapsablePanel >
          )
        })
      )}
    </>
  )
})